import { ActionsPart, APIPart, PartTypes, QuotaActionsPart, SMSQuestionPart } from '@/app/module/campaigns/types'
import { processActionForSaving } from '@/app/module/campaigns/utils/process-actions'

import type { ActionContainer } from '../types'

export type SenderIdConfig = {
  senderId: string
  senderIdQuestions: string
  senderIdWhatsapp?: string
}

type ActionList = ActionContainer['actions']

type NonNullableActionList = NonNullable<ActionList>

type SMSQuestionAnswers = SMSQuestionPart[PartTypes.SMSQuestion]['onAnswer']

function prepareActionList(actions: NonNullableActionList, senderIds: SenderIdConfig): NonNullableActionList
function prepareActionList(actions: ActionList, senderIds: SenderIdConfig): ActionList
function prepareActionList(actions: ActionList, senderIds: SenderIdConfig): ActionList {
  if (!actions) {
    return actions
  }

  return processActionForSaving({ actions }, senderIds).actions
}

const getPreparedActionsPatch = (container: ActionContainer<NonNullableActionList>, senderIds: SenderIdConfig) =>
  container.actions === undefined
    ? {}
    : {
        actions: prepareActionList(container.actions, senderIds),
      }

function prepareSMSQuestionAnswers(answers: SMSQuestionAnswers, senderIds: SenderIdConfig): SMSQuestionAnswers
function prepareSMSQuestionAnswers(
  answers: SMSQuestionAnswers | null | undefined,
  senderIds: SenderIdConfig,
): SMSQuestionAnswers | null | undefined
function prepareSMSQuestionAnswers(
  answers: SMSQuestionAnswers | null | undefined,
  senderIds: SenderIdConfig,
): SMSQuestionAnswers | null | undefined {
  if (!answers) {
    return answers
  }

  return answers.map((answer) => ({
    ...answer,
    ...getPreparedActionsPatch(answer, senderIds),
  }))
}

export const prepareSMSQuestion = (part: SMSQuestionPart, senderIds: SenderIdConfig): SMSQuestionPart => {
  const smsQuestion = part[PartTypes.SMSQuestion]
  const openEnded = smsQuestion.openEnded

  return {
    ...part,
    [PartTypes.SMSQuestion]: {
      ...smsQuestion,
      ...(smsQuestion.onAnswer !== undefined && {
        onAnswer: prepareSMSQuestionAnswers(smsQuestion.onAnswer, senderIds),
      }),
      onInvalidReply: {
        ...smsQuestion.onInvalidReply,
        ...getPreparedActionsPatch(smsQuestion.onInvalidReply, senderIds),
      },
      onRetriesExhausted: {
        ...smsQuestion.onRetriesExhausted,
        ...getPreparedActionsPatch(smsQuestion.onRetriesExhausted, senderIds),
      },
      onTimeout: {
        ...smsQuestion.onTimeout,
        ...getPreparedActionsPatch(smsQuestion.onTimeout, senderIds),
      },
      ...(openEnded && {
        openEnded: {
          ...openEnded,
          ...getPreparedActionsPatch(openEnded, senderIds),
          ...(openEnded.categories && {
            categories: openEnded.categories.map((category) => ({
              ...category,
              ...getPreparedActionsPatch(category, senderIds),
            })),
          }),
        },
      }),
    },
  }
}

export const prepareActionsOnly = (part: ActionsPart, senderIds: SenderIdConfig): ActionsPart => ({
  ...part,
  [PartTypes.ActionsOnly]: {
    ...part[PartTypes.ActionsOnly],
    actions: prepareActionList(part[PartTypes.ActionsOnly].actions, senderIds),
  },
})

export const prepareQuotaActions = (part: QuotaActionsPart, senderIds: SenderIdConfig): QuotaActionsPart => ({
  ...part,
  [PartTypes.QuotaActions]: {
    ...part[PartTypes.QuotaActions],
    onExhausted: prepareActionList(part[PartTypes.QuotaActions].onExhausted, senderIds),
    onOpen: prepareActionList(part[PartTypes.QuotaActions].onOpen, senderIds),
  },
})

export const prepareAPI = (part: APIPart, senderIds: SenderIdConfig): APIPart => {
  const apiCall = part[PartTypes.ApiCall]

  return {
    ...part,
    [PartTypes.ApiCall]: {
      ...apiCall,
      ...(apiCall.onError !== undefined && {
        onError: prepareActionList(apiCall.onError, senderIds),
      }),
      ...(apiCall.onSuccess !== undefined && {
        onSuccess: prepareActionList(apiCall.onSuccess, senderIds),
      }),
    },
  }
}
